import { Spinner } from '@tempmee/react';
import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import { Box, DataGrid, Pagination } from '~/commonComponents';
import { getNumberOfCandidates } from '~/components/JobList/utils';
import { DashboardJobsStatus, DashboardJobsType, IDashboardJob } from '~/domain/DashboardJobs';
import { getShouldEnforcePermanentJobV2 } from '~/services/posthog/FeatureFlagUtils';
import { fireActionNeededShiftViewShiftClicked } from '~/posthogs/events';
import { FilterStatusEnum } from '~/utils/Enum';
import { useJobList } from '../../hooks';
import { InviteProfessional } from '~/commonComponents/InviteProfessional';
import { CancelShift } from '../CancelShift';
import { DataGridCommonColumn } from '../DataGrid.column';
import { EmptyMessage } from '../EmptyMessage';
import { ExpirationUpdatePopup } from './ExpirationUpdatePopup';

interface IPendingTabProps {
  jobType: DashboardJobsType;
}

export const PendingTab = ({ jobType }: IPendingTabProps) => {
  const navigate = useNavigate();

  const isEnforcePermanentJobV2 = useRef(getShouldEnforcePermanentJobV2()).current;

  const { data, isInitialLoading, isLoading, methods, pagination } = useJobList({
    status: DashboardJobsStatus.ACTION_REQUIRED,
    jobType,
  });

  const handleRowClick = useCallback(
    (item: IDashboardJob) => {
      const counters = item?.multipleCounters ?? [];
      const favoriteCandidate = counters.find((counter) => !!counter.isFavorite);

      const candidateId = (favoriteCandidate ?? counters?.[0])?.candidate?.id;
      const shiftId = item?.id;

      if (candidateId && shiftId) {
        fireActionNeededShiftViewShiftClicked({ shiftId, candidateCount: counters.length });
        navigate(`/dashboard/job/${item?.id}/confirm/${candidateId}`);
      }
    },
    [navigate],
  );

  if (isInitialLoading) {
    return (
      <Box marginTop={32}>
        <Spinner centered variant="default" size="md" />
      </Box>
    );
  }

  return (
    <>
      <DataGrid
        EmptyComponent={<EmptyMessage type={FilterStatusEnum.PENDING} />}
        PaginationComponent={
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={methods.setCurrentPage}
            maxButtons={5}
          />
        }
        columns={[
          DataGridCommonColumn.Office,
          ...(isEnforcePermanentJobV2
            ? [
                {
                  ...DataGridCommonColumn.Type,
                  header: 'Type',
                  width: '120px',
                },
              ]
            : []),
          DataGridCommonColumn.Details,
          {
            ...DataGridCommonColumn.Profession,
            width: '160px',
          },
          {
            key: 'candidates',
            header: 'Candidates',
            width: '120px',
            align: 'center',
            render: (item) => getNumberOfCandidates(item.multipleCounters),
          },
          DataGridCommonColumn.Rate,
          {
            ...DataGridCommonColumn.Invite,
            render: (item) => {
              if (item.dashboardJobApi.isOnsiteInterview) {
                return null;
              }
              return <InviteProfessional shift={item.dashboardJobApi} size="icon" source="shifts_action_needed" />;
            },
          },
          {
            key: 'more',
            header: 'More',
            width: '50px',
            align: 'center',
            render: (item) => <CancelShift item={item} />,
          },
          DataGridCommonColumn.Action,
        ]}
        data={data}
        dataCy={{
          root: 'action-needed-table',
          rowItem: 'pending-shift-row',
        }}
        isLoading={isLoading}
        keyExtractor={(item) => item.id}
        onRowClick={handleRowClick}
      />
      <ExpirationUpdatePopup />
    </>
  );
};
